import { useEffect, useId, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties } from 'react';

import { animate } from 'framer-motion';

/*
 * Status tag: the listing status tags from the designer's tags-v4 handoff,
 * kept one-to-one. A tag rests as a white pill with a grey label; hover brings
 * the coloured surface 45% of the way in and a click toggles it fully on. The
 * active surface is the Figma SVG export, parameterised, so the stroke
 * gradients match exactly. Trending's icon morphs between a house and a
 * trending-up line.
 */

// ---- the transition and hover strength ----------------------------------------------
const FEEL = { duration: 0.28, ease: 'cubic-bezier(0.2, 0, 0, 1)', hover: 0.45 };
const FOCUS = '#0040E6';

/** Page-level rule for the keyboard focus ring. Render once. */
export const StatusTagStyles = () => (
  <style>{`.od-tag:focus-visible{ outline:2px solid ${FOCUS}; outline-offset:2px; }`}</style>
);

// ---- the inactive state --------------------------------------------------------------
const REST = { ink: '#89867F', stroke: '#EAE8E0', fill: '#FFFFFF' };

/* ---------------- the icon: house → trending-up, drawn ---------------- */
const ROOF = 'M6 9L12 3L16 7';
const LEFT =
  'M6 9L3 12H5V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H12';
const RIGHT =
  'M16 7L21 12H19V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H12';
const DOOR =
  'M9 21V15C9 14.4696 9.21071 13.9609 9.58579 13.5858C9.96086 13.2107 10.4696 13 11 13H13C13.5304 13 14.0391 13.2107 14.4142 13.5858C14.7893 13.9609 15 14.4696 15 15V21';
type Point = [number, number];
const VALLEY: Point = [16, 7];
const CORNER: Point = [24, -1];
const ARROW_DOWN: Point = [24, 6];
const ARROW_LEFT: Point = [17, -1];
const TREND_SHIFT: Point = [-3, 8];
// each part's window on the master progress, its ease, and the morph curve
const W = {
  doorStart: 0,
  doorEnd: 0.3,
  bodyStart: 0.05,
  bodyEnd: 0.5,
  shiftStart: 0.25,
  shiftEnd: 0.7,
  lineStart: 0.4,
  lineEnd: 0.8,
  arrowStart: 0.7,
  arrowEnd: 1,
};
const SEGMENT_EASE: [number, number, number, number] = [0.45, 0, 0.55, 1];
const MORPH = { duration: 1.2, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] };
const BACK = { duration: 1.0, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpPt = (p: Point, q: Point, t: number): Point => [lerp(p[0], q[0], t), lerp(p[1], q[1], t)];
const pt = (p: Point) => `${p[0].toFixed(3)} ${p[1].toFixed(3)}`;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const window01 = (t: number, s: number, e: number) => clamp01((t - s) / (e - s));
const bezierEase = ([x1, y1, x2, y2]: [number, number, number, number]) => {
  const ax = 1 - 3 * x2 + 3 * x1;
  const bx = 3 * x2 - 6 * x1;
  const cx = 3 * x1;
  const ay = 1 - 3 * y2 + 3 * y1;
  const by = 3 * y2 - 6 * y1;
  const cy = 3 * y1;
  const sx = (u: number) => ((ax * u + bx) * u + cx) * u;
  const sy = (u: number) => ((ay * u + by) * u + cy) * u;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let lo = 0;
    let hi = 1;
    let u = x;
    for (let i = 0; i < 24; i++) {
      u = (lo + hi) / 2;
      if (sx(u) < x) lo = u;
      else hi = u;
    }
    return sy(u);
  };
};
const ease = bezierEase(SEGMENT_EASE);
const frame = (t: number) => {
  const door = 1 - ease(window01(t, W.doorStart, W.doorEnd));
  const body = 1 - ease(window01(t, W.bodyStart, W.bodyEnd));
  const shift = ease(window01(t, W.shiftStart, W.shiftEnd));
  const line = ease(window01(t, W.lineStart, W.lineEnd));
  const arrow = ease(window01(t, W.arrowStart, W.arrowEnd));
  return {
    line: `${ROOF}L${pt(lerpPt(VALLEY, CORNER, line))}`,
    arrow,
    door,
    body,
    offset: lerpPt([0, 0], TREND_SHIFT, shift),
  };
};
const dash = (el: SVGPathElement | null, visible: number, offset = 0) => {
  if (!el) return;
  el.style.strokeDasharray = `${visible.toFixed(4)} 1`;
  el.style.strokeDashoffset = offset.toFixed(4);
  el.style.opacity = visible > 0.001 ? '1' : '0';
};

/** The morphing icon. `target` is 1 (trending) or 0 (house); it always
 *  animates from its current progress, so changes mid-flight stay smooth. */
const TrendingIcon = ({
  size = 12,
  target,
  initial = 0,
}: {
  size?: number;
  target: number;
  initial?: number;
}) => {
  const g = useRef<SVGGElement>(null);
  const line = useRef<SVGPathElement>(null);
  const down = useRef<SVGPathElement>(null);
  const left = useRef<SVGPathElement>(null);
  const l = useRef<SVGPathElement>(null);
  const r = useRef<SVGPathElement>(null);
  const door = useRef<SVGPathElement>(null);
  const anim = useRef<ReturnType<typeof animate> | null>(null);
  const t = useRef(initial); // current progress; 0 = house (draws in on mount), 1 = already trending
  useEffect(() => {
    const apply = (v: number) => {
      t.current = v;
      const f = frame(v);
      line.current?.setAttribute('d', f.line);
      dash(down.current, f.arrow);
      dash(left.current, f.arrow);
      dash(door.current, f.door, -(1 - f.door) / 2);
      dash(l.current, f.body);
      dash(r.current, f.body);
      g.current?.setAttribute(
        'transform',
        `translate(${f.offset[0].toFixed(3)} ${f.offset[1].toFixed(3)})`,
      );
    };
    anim.current?.stop();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      apply(target);
      return undefined;
    }
    const from = t.current;
    const curve = target > from ? MORPH : BACK;
    const dist = Math.abs(target - from);
    if (dist < 0.001) return undefined;
    anim.current = animate(from, target, {
      duration: curve.duration * dist,
      ease: curve.ease,
      onUpdate: apply,
    });
    return () => anim.current?.stop();
  }, [target]);
  const hidden = { strokeDasharray: '1 1' };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ display: 'block', flex: 'none' }}
    >
      <g
        ref={g}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={`translate(${TREND_SHIFT[0]} ${TREND_SHIFT[1]})`}
      >
        <path ref={door} d={DOOR} pathLength="1" style={hidden} />
        <path ref={l} d={LEFT} pathLength="1" style={hidden} />
        <path ref={r} d={RIGHT} pathLength="1" style={hidden} />
        <path ref={line} d={`${ROOF}L${pt(CORNER)}`} />
        <path ref={down} d={`M${pt(CORNER)}L${pt(ARROW_DOWN)}`} pathLength="1" style={hidden} />
        <path ref={left} d={`M${pt(CORNER)}L${pt(ARROW_LEFT)}`} pathLength="1" style={hidden} />
      </g>
    </svg>
  );
};

/* ---------------- palette (design hex; swap for tokens when they exist) ---------------- */
type Colors = { ink: string; fill: [string, string]; stroke: string; fade: string };
const SHADOW = '#231E1B';
const PALETTE: Record<StatusTagVariant, Colors> = {
  trending: { ink: '#9F0101', fill: ['#F8DCC2', '#F3C9A2'], stroke: '#EE7E2E', fade: '#9F0101' },
  pendingSale: { ink: '#3C2B05', fill: ['#F9F5CB', '#F5EFA1'], stroke: '#CECC5F', fade: '#3C2B05' },
  newlyListed: { ink: '#020E60', fill: ['#E9F1FA', '#CDE4FF'], stroke: '#63A5F8', fade: '#F9FCFF' },
  comingSoon: { ink: '#3C3733', fill: ['#EAE8E0', '#D7D4CD'], stroke: '#BDBAB3', fade: '#F9F8F5' },
  priceDrop: { ink: '#1D5210', fill: ['#EBF9E8', '#CEF5C4'], stroke: '#9BCC8F', fade: '#F9F8F5' },
};

/* ---------------- variants (geometry from the Figma SVG exports) ----------------
   width: the export's pill width. shadow: the label's inner-shadow opacity.
   fades: the two stroke gradients (handle coordinates in pill space, plus opacity). */
type Fade = { x1: number; y1: number; x2: number; y2: number; opacity: number };
type Variant = {
  label: string;
  icon: boolean;
  padding: [number, number];
  width: number;
  shadow: number;
  fades: Fade[];
};
export type StatusTagVariant =
  | 'trending'
  | 'pendingSale'
  | 'newlyListed'
  | 'comingSoon'
  | 'priceDrop';
const VARIANTS: Record<StatusTagVariant, Variant> = {
  trending: {
    label: 'Trending',
    icon: true,
    padding: [8, 10],
    width: 85,
    shadow: 0.2,
    fades: [
      { x1: 72.6042, y1: -3.5, x2: 68.3777, y2: 2.98166, opacity: 1 },
      { x1: -2.27635, y1: -2, x2: 11.0063, y2: 18.3701, opacity: 1 },
    ],
  },
  pendingSale: {
    label: 'Pending sale',
    icon: false,
    padding: [10, 10],
    width: 79,
    shadow: 0.5,
    fades: [
      { x1: 67.4792, y1: -3.5, x2: 63.136, y2: 2.69047, opacity: 0.3 },
      { x1: -2.11567, y1: -2, x2: 11.5338, y2: 17.455, opacity: 0.3 },
    ],
  },
  newlyListed: {
    label: 'Newly listed',
    icon: false,
    padding: [10, 10],
    width: 86,
    shadow: 0.4,
    fades: [
      { x1: 13.0183, y1: 29, x2: 19.0227, y2: 18.501, opacity: 0.5 },
      { x1: 20.9083, y1: -2, x2: 19.4687, y2: 8.29879, opacity: 0.4 },
    ],
  },
  priceDrop: {
    label: 'Price drop',
    icon: false,
    padding: [10, 10],
    width: 76,
    shadow: 0.2,
    fades: [
      { x1: 11.5046, y1: 29, x2: 17.8597, y2: 19.1798, opacity: 0.2 },
      { x1: 18.4771, y1: -2, x2: 16.8569, y2: 8.24374, opacity: 0.3 },
    ],
  },
  comingSoon: {
    label: 'Coming soon',
    icon: false,
    padding: [10, 10],
    width: 93,
    shadow: 0.2,
    fades: [
      { x1: 14.078, y1: 29, x2: 19.836, y2: 18.1122, opacity: 0.6 },
      { x1: 22.6101, y1: -2, x2: 21.2752, y2: 8.32746, opacity: 0.7 },
    ],
  },
};

/* SVG inner-shadow filter for the label: SourceAlpha offset + blurred, cut
   out of the glyphs, filled with the shadow colour, merged over the text.
   Figma: 0 2px, blur 2 (= stdDeviation 1), #231E1B at `opacity`. */
const InnerShadowFilter = ({ id, opacity }: { id: string; opacity: number }) => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
    <defs>
      <filter id={id} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
        <feOffset in="SourceAlpha" dx="0" dy="2" result="off" />
        <feGaussianBlur in="off" stdDeviation="1" result="blur" />
        <feComposite in="SourceAlpha" in2="blur" operator="out" result="inset" />
        <feFlood floodColor={SHADOW} floodOpacity={opacity} result="tint" />
        <feComposite in="tint" in2="inset" operator="in" result="shadow" />
        <feMerge>
          <feMergeNode in="SourceGraphic" />
          <feMergeNode in="shadow" />
        </feMerge>
      </filter>
    </defs>
  </svg>
);

/* The pill surface: Figma's export, parameterised. `width` × 28, rx 13.5, 1px inner stroke. */
const TagSurface = ({
  id,
  width,
  colors,
  fades,
}: {
  id: string;
  width: number;
  colors: Colors;
  fades: Fade[];
}) => {
  const rect = { x: 0.5, y: 0.5, width: width - 1, height: 27, rx: 13.5 };
  return (
    <svg
      viewBox={`0 0 ${width} 28`}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      aria-hidden="true"
      fill="none"
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}
    >
      <rect {...rect} fill={`url(#${id}-fill)`} />
      <rect {...rect} stroke={colors.stroke} />
      {fades.map((fade, index) => (
        <rect
          key={index}
          {...rect}
          stroke={`url(#${id}-fade-${index})`}
          strokeOpacity={fade.opacity}
        />
      ))}
      <defs>
        <linearGradient
          id={`${id}-fill`}
          x1="0"
          y1="14"
          x2={width}
          y2="14"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors.fill[0]} />
          <stop offset="1" stopColor={colors.fill[1]} />
        </linearGradient>
        {fades.map((fade, index) => (
          <linearGradient
            key={index}
            id={`${id}-fade-${index}`}
            x1={fade.x1}
            y1={fade.y1}
            x2={fade.x2}
            y2={fade.y2}
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={colors.fade} />
            <stop offset="1" stopColor={colors.fade} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
    </svg>
  );
};

type StatusTagProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onToggle'> & {
  variant: StatusTagVariant;
  label?: string;
  /** Controlled state; pair with onToggle. */
  active?: boolean;
  defaultActive?: boolean;
  onToggle?: (active: boolean) => void;
};

/**
 * A listing status tag that rests inactive. Hover previews, click toggles.
 */
export const StatusTag = ({
  variant,
  label,
  active: activeProp,
  defaultActive = false,
  onToggle,
  className,
  style,
  ...rest
}: StatusTagProps) => {
  const v = VARIANTS[variant];
  const colors = PALETTE[variant];
  const id = useId().replaceAll(':', '');
  const [inner, setInner] = useState(defaultActive);
  const active = activeProp ?? inner;
  const [hover, setHover] = useState(false);
  let a = 0; // the one value that drives every layer
  if (active) a = 1;
  else if (hover) a = FEEL.hover;
  const t = `${FEEL.duration}s ${FEEL.ease}`;
  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fade = (o: number): CSSProperties => ({
    opacity: o,
    transition: reduced ? 'none' : `opacity ${t}`,
  });
  const labelStyle: CSSProperties = {
    font: '500 12px/12px var(--od-font)',
    letterSpacing: '-0.03em',
    whiteSpace: 'nowrap',
  };
  const toggle = () => {
    const next = !active;
    if (activeProp === undefined) setInner(next);
    onToggle?.(next);
  };
  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);
  return (
    <button
      type="button"
      className={className ? `od-tag ${className}` : 'od-tag'}
      aria-pressed={active}
      {...rest}
      onClick={toggle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingLeft: v.padding[0],
        paddingRight: v.padding[1],
        paddingTop: 8,
        paddingBottom: 8,
        minWidth: v.width,
        height: 28,
        boxSizing: 'border-box',
        borderRadius: 14,
        isolation: 'isolate',
        border: 0,
        background: 'transparent',
        font: 'inherit',
        outline: 'none',
        ...style,
      }}
    >
      {/* inactive surface: always underneath */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 14,
          background: REST.fill,
          boxShadow: `inset 0 0 0 1px ${REST.stroke}`,
        }}
      />
      {/* active surface: fades in over it */}
      <span
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, borderRadius: 14, overflow: 'hidden', ...fade(a) }}
      >
        <TagSurface id={id} width={v.width} colors={colors} fades={v.fades} />
      </span>
      {v.icon ? (
        <span
          style={{
            position: 'relative',
            display: 'flex',
            marginRight: 6,
            color: `color-mix(in srgb, ${colors.ink} ${Math.round(a * 100)}%, ${REST.ink})`,
            transition: reduced ? 'none' : `color ${t}`,
          }}
        >
          <TrendingIcon size={12} initial={0} target={hover ? 0 : 1} />
        </span>
      ) : null}
      <InnerShadowFilter id={`${id}-inset`} opacity={v.shadow} />
      {/* the label: grey and inked versions stacked, crossfading */}
      <span style={{ position: 'relative', display: 'grid' }}>
        <span style={{ ...labelStyle, gridArea: '1 / 1', color: REST.ink, ...fade(1 - a) }}>
          {label ?? v.label}
        </span>
        <span
          aria-hidden="true"
          style={{
            ...labelStyle,
            gridArea: '1 / 1',
            color: colors.ink,
            filter: `url(#${id}-inset)`,
            ...fade(a),
          }}
        >
          {label ?? v.label}
        </span>
      </span>
    </button>
  );
};

/**
 * A listing status shown, not chosen: the tag's active look as a plain span,
 * for the map cards and the pin preview.
 */
export const StatusBadge = ({
  variant,
  label,
  className,
  style,
}: {
  variant: StatusTagVariant;
  label?: string;
  className?: string;
  style?: CSSProperties;
}) => {
  const v = VARIANTS[variant];
  const colors = PALETTE[variant];
  const id = useId().replaceAll(':', '');
  return (
    <span
      className={className ? `od-tag ${className}` : 'od-tag'}
      style={{
        // The sheet and the bar define the font variable; the cards do not.
        ['--od-font' as string]: "var(--font-sans, 'Graphik', sans-serif)",
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        paddingLeft: v.padding[0],
        paddingRight: v.padding[1],
        paddingTop: 8,
        paddingBottom: 8,
        minWidth: v.width,
        height: 28,
        boxSizing: 'border-box',
        borderRadius: 14,
        isolation: 'isolate',
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, borderRadius: 14, overflow: 'hidden' }}
      >
        <TagSurface id={id} width={v.width} colors={colors} fades={v.fades} />
      </span>
      {v.icon ? (
        <span style={{ position: 'relative', display: 'flex', marginRight: 6, color: colors.ink }}>
          <TrendingIcon size={12} initial={1} target={1} />
        </span>
      ) : null}
      <InnerShadowFilter id={`${id}-inset`} opacity={v.shadow} />
      <span
        style={{
          position: 'relative',
          font: '500 12px/12px var(--od-font)',
          letterSpacing: '-0.03em',
          whiteSpace: 'nowrap',
          color: colors.ink,
          filter: `url(#${id}-inset)`,
        }}
      >
        {label ?? v.label}
      </span>
    </span>
  );
};
